import { useTheme, useMediaQuery, Typography, Stack, Grid } from "@mui/material";
import { ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  BadgeGoals,
  Modal
} from "../../components";
import { ButtonDigited } from "../../components/Button/Button.variants";
import { useDownloadCertificate } from "../../queries";
import { useNavbarStore } from "../../stores";
import type { UserAchievement } from "../../types";
import { getError } from "../../utils/error";
import { downloadBlobFile } from "../../utils/general";
import { CertificateInfo } from "../CertificateButton/CertificateButtonContainer.types";

export function ContentGoals ({ learnerAchievements }:{learnerAchievements: UserAchievement[] }){

  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const setNavbarItem = useNavbarStore(state => state.setNavbarItem);
  const [certificatesInDownload, setCertificatesInDownload] = useState<number[] | null>(null);
  const [showModal, setShowModal] = useState(false);



  const {
    error: downloadCertificateError,
    isError: isDownloadCertificateError,
    mutateAsync: mutateDownloadCertificate } = useDownloadCertificate();

  useEffect(() => {
    isDownloadCertificateError && setShowModal(true);
  }, [isDownloadCertificateError]);

  const downloadCertificateHandler = async (certificateInfo: CertificateInfo)=>{
    setCertificatesInDownload(certificatesInDownload => {
      return certificatesInDownload ? [...certificatesInDownload, certificateInfo.certificateId]
        : [certificateInfo.certificateId];
    });

    try {
      const certificateResponseBlob = await mutateDownloadCertificate(certificateInfo);
      downloadBlobFile({
        blobFile: certificateResponseBlob,
        fileName: `issued_certificate_lo_${certificateInfo.learningObjectId}_learner_${certificateInfo.learnerId}.pdf`
      });
    }finally {
      setCertificatesInDownload(certificatesInDownload => {
        if (certificatesInDownload && certificatesInDownload.length > 0){
          const filteredIds = certificatesInDownload.filter(id => id !== certificateInfo.certificateId);
          if (filteredIds.length < 1){
            return null;
          }
        }
        return null;
      });
    }
  };

  return (

    <Grid
      gap={ "2.5rem" }
      display={ "flex" }
      flexDirection={ "row" }
      flexWrap={ "wrap" }
    >
      {
        (!learnerAchievements || !(learnerAchievements.length > 0 )) ? (
          <>
            <Typography
              fontSize={ isMobile ? "1rem" : "1.5rem" }
              fontWeight={ 700 }
              lineHeight={ "1.25" }>
              { t("no_certification") }
            </Typography>
            <Typography
              fontSize={ isMobile ? "0.875rem" : "1.25rem" }
              lineHeight={ isMobile ? "1.3125" : "1.05" }
            >
              { t( "new_certifications") }
            </Typography>
            <ButtonDigited
              aria-label={ t("go_to_explore") }
              typology="tertiary"
              href={ "/esplora" }
              sx={ {
                gap:"0.75rem",
                padding: 0
              } }
              onClick={ ()=> {
                setNavbarItem("EXPLORE");
              } }
              text={
                <Stack display="flex" flexDirection="row" alignItems="center" aria-hidden="true">
                  { t("go_to_explore") }
                  <ChevronRight size="0.85rem" color={ theme.customColors.systemSecondary01 } />
                </Stack>
              }
            />

          </>
        ) : (
          learnerAchievements?.map((badge) => (
            <BadgeGoals
              key={ `badge-${badge.certificate?.id || badge.badge?.id}` }
              small={ false }
              badgeDisabled={ true }
              badge={ badge.badge }
              certificate={ badge.certificate }
              downloadCertificate={ downloadCertificateHandler }
              isDownloading={ !!( badge.certificate?.id &&
                certificatesInDownload &&
                certificatesInDownload.indexOf(badge.certificate?.id)>=0)
              }
            />
          ))
        )
      }
      <Modal
        open={ showModal && isDownloadCertificateError }
        onClose={ ()=> { setShowModal(false) } }
        description={ getError(downloadCertificateError)?.[0]?.message ?? "" }
        title={ getError(downloadCertificateError)?.[0]?.name ?? "" }
      />
    </Grid>
  );
}
